import { useTranslation } from 'next-i18next'

import { isAuthenticated, logoutUser } from '~/auth/slices/auth.slice'
import appConfig from '~/config/app'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { closeMenu } from '~/redux/slices/menu'
import useSchoolSignOut from '~/classroom/hooks/useSchoolSignOut'

export default function ButtonLogout({ className }: { className?: string }): React.ReactElement {
  const authenticated = useAppSelector(isAuthenticated)
  const dispatch = useAppDispatch()
  const { t } = useTranslation('menu')

  /**
   * ⚠ Two behaviours on purpose, split on the build-time edition flag.
   *
   * The school edition follows `TeacherAvatarMenu` and `ClassroomAvatarMenu`: the full session clear
   * plus a redirect. Both halves are the fix for #869em17v5 — no page a pupil can reach is behind a
   * guard that re-checks on its own, so `logoutUser` alone left the pupil on `/dashboard`, which
   * renders its signed-out branch: a b2c subscription pitch on a school deploy. `/` is role
   * selection in this edition, so it is where signing out belongs. `replace`, not `push`, so Back
   * does not return to the dashboard — S-11 asks for that explicitly.
   *
   * `clearBrowserSession` rather than `logoutUser` because a school device is shared: the persist
   * snapshot and the query cache outlive a token-only clear, and the next pupil at that desk would
   * inherit the previous pupil's curriculum, chat and checklist state.
   *
   * ⚠ **This is the PUPIL's sign-out.** A pupil signs in through single sign-on and lands on
   * `/dashboard`, whose menu is this one — not either teacher avatar menu. So this is the button the
   * shared-Chromebook defence most needs to reach: without it, signing out here leaves the district's
   * Okta session alive and the next child at this desk is admitted as her.
   *
   * ⚠ The note that used to sit here said the clear was "not awaited, so a slow or failing logout
   * call cannot strand the pupil". `useSchoolSignOut` has to await, because the provider can only be
   * asked while we still hold a token — it bounds that wait and falls through to a local sign-out on
   * any failure, which keeps the same guarantee.
   *
   * The b2c edition keeps the original token-only logout in place: this button also sits on the
   * family panel and the profile switcher, whose signed-out behaviour is not in scope here.
   */
  const signOutOfSchool = useSchoolSignOut(() => {
    dispatch(closeMenu())
  })

  const handleLogOut = (): void => {
    if (!appConfig.isSchoolPlatform) {
      dispatch(logoutUser())
      return
    }

    signOutOfSchool()
  }

  return (
    <button
      className={`${authenticated ? 'block' : 'hidden'} ${className}`}
      data-test-id="menu-logout"
      onClick={handleLogOut}
    >
      {t('logOut')}
    </button>
  )
}
